'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';

import { type PhysicalAddressFieldName } from '../components/inputs-extended/physical-address-fields/PhysicalAddressFields';
import { useAddressLookup } from './useAddressLookup';

type PhysicalAddressRevealMode = 'all-fields' | 'progressive';

const DEFAULT_COLLAPSED_FIELD_ORDER: PhysicalAddressFieldName[] = [
  'organizationName',
  'country',
  'addressLine1',
];
const DEFAULT_EXPANDED_FIELD_ORDER: PhysicalAddressFieldName[] = [
  'organizationName',
  'country',
  'addressLine1',
  'addressLine2',
  'cityTown',
  'stateProvince',
  'zipPostalCode',
];

interface UsePhysicalAddressFormOptions {
  mode?: PhysicalAddressRevealMode;
  collapsedFieldOrder?: PhysicalAddressFieldName[];
  expandedFieldOrder?: PhysicalAddressFieldName[];
  formKeyPrefix?: string;
  hasOrganizationName?: boolean;
  isAddressLookupEnabled?: boolean;
  shouldUseCombobox?: boolean;
  isDefaultExpanded?: boolean;
  onEnterManually?: () => void;
}

const usePhysicalAddressForm = ({
  mode = 'all-fields',
  collapsedFieldOrder = DEFAULT_COLLAPSED_FIELD_ORDER,
  expandedFieldOrder = DEFAULT_EXPANDED_FIELD_ORDER,
  formKeyPrefix,
  hasOrganizationName = false,
  isAddressLookupEnabled = true,
  shouldUseCombobox = false,
  isDefaultExpanded = false,
  onEnterManually,
}: UsePhysicalAddressFormOptions = {}) => {
  const [hasManuallyExpanded, setHasManuallyExpanded] = useState(false);
  const [shouldValidateExpandedFields, setShouldValidateExpandedFields] = useState(false);
  const [isCountryComboboxOpen, setIsCountryComboboxOpen] = useState(false);
  const [isStateComboboxOpen, setIsStateComboboxOpen] = useState(false);
  const [dropdownContainer, setDropdownContainer] = useState<HTMLDivElement | null>(null);
  const lastHandledSubmitCountRef = useRef(0);

  const addressLookup = useAddressLookup({
    formKeyPrefix,
    isEnabled: isAddressLookupEnabled,
  });

  const formContext = useFormContext();
  const { clearErrors, control, trigger } = formContext;
  const addressLine1Name = formKeyPrefix ? `${formKeyPrefix}.addressLine1` : 'addressLine1';
  const cityName = formKeyPrefix ? `${formKeyPrefix}.city` : 'city';
  const stateCodeName = formKeyPrefix ? `${formKeyPrefix}.stateCode` : 'stateCode';
  const stateNameName = formKeyPrefix ? `${formKeyPrefix}.stateName` : 'stateName';
  const postalCodeName = formKeyPrefix ? `${formKeyPrefix}.postalCode` : 'postalCode';
  const { defaultValues, errors, isSubmitted, isSubmitSuccessful, submitCount } = useFormState({
    control,
  });
  const addressLine1 = useWatch({ control, name: addressLine1Name, disabled: !control });
  const city = useWatch({ control, name: cityName, disabled: !control });
  const addressLine1Error = get(errors, addressLine1Name);
  const hasExistingAddress = Boolean(
    get(defaultValues, addressLine1Name)?.trim() && get(defaultValues, cityName)?.trim()
  );

  const handleDropdownContainerRef = useCallback((node: HTMLDivElement | null) => {
    setDropdownContainer(node);
  }, []);

  const handleExpand = useCallback(() => {
    setHasManuallyExpanded(true);
  }, []);

  const handleEnterManually = useCallback(() => {
    onEnterManually?.();
    handleExpand();
  }, [handleExpand, onEnterManually]);

  const handleSelectPlaceId = useCallback(
    (placeId: string) => {
      addressLookup?.onSelectPlaceId?.(placeId);
      handleExpand();
    },
    [addressLookup, handleExpand]
  );

  useLayoutEffect(() => {
    if (mode !== 'progressive' || !isSubmitted) return;
    if (lastHandledSubmitCountRef.current === submitCount) return;

    const street = addressLine1?.trim() ?? '';
    const cityValue = city?.trim() ?? '';

    if (!addressLine1Error) {
      if (isSubmitSuccessful) lastHandledSubmitCountRef.current = submitCount;
      return;
    }

    if (!street || cityValue) {
      lastHandledSubmitCountRef.current = submitCount;
      return;
    }

    lastHandledSubmitCountRef.current = submitCount;
    clearErrors(addressLine1Name);
    setHasManuallyExpanded(true);
    setShouldValidateExpandedFields(true);
  }, [
    addressLine1,
    addressLine1Error,
    addressLine1Name,
    city,
    clearErrors,
    isSubmitted,
    isSubmitSuccessful,
    mode,
    submitCount,
  ]);

  useLayoutEffect(() => {
    if (!shouldValidateExpandedFields || !hasManuallyExpanded) return;

    setShouldValidateExpandedFields(false);
    void trigger([cityName, stateCodeName, stateNameName, postalCodeName]);
  }, [
    cityName,
    hasManuallyExpanded,
    postalCodeName,
    shouldValidateExpandedFields,
    stateCodeName,
    stateNameName,
    trigger,
  ]);

  const isExpanded =
    mode === 'all-fields' || isDefaultExpanded || hasManuallyExpanded || hasExistingAddress;

  const fieldOrder = useMemo(() => {
    if (mode === 'all-fields') {
      const order = hasOrganizationName
        ? expandedFieldOrder
        : expandedFieldOrder.filter((field) => field !== 'organizationName');
      return order;
    }

    const collapsed = hasOrganizationName
      ? collapsedFieldOrder
      : collapsedFieldOrder.filter((field) => field !== 'organizationName');
    const expanded = hasOrganizationName
      ? expandedFieldOrder
      : expandedFieldOrder.filter((field) => field !== 'organizationName');

    return isExpanded ? expanded : collapsed;
  }, [collapsedFieldOrder, expandedFieldOrder, hasOrganizationName, isExpanded, mode]);

  const addressAutocompleteProps = addressLookup
    ? {
        ...addressLookup,
        onSelectPlaceId: handleSelectPlaceId,
        onEnterManually: handleEnterManually,
      }
    : undefined;

  const comboboxState = shouldUseCombobox
    ? {
        country: {
          isOpen: isCountryComboboxOpen,
          onOpenChange: setIsCountryComboboxOpen,
        },
        stateProvince: {
          isOpen: isStateComboboxOpen,
          onOpenChange: setIsStateComboboxOpen,
        },
      }
    : undefined;

  return {
    addressAutocompleteProps,
    comboboxState,
    dropdownContainer,
    dropdownContainerRef: handleDropdownContainerRef,
    fieldOrder,
    isExpanded,
    handleEnterManually,
  };
};

export {
  DEFAULT_COLLAPSED_FIELD_ORDER,
  DEFAULT_EXPANDED_FIELD_ORDER,
  type PhysicalAddressRevealMode,
  usePhysicalAddressForm,
  type UsePhysicalAddressFormOptions,
};
